import React, { useEffect, useImperativeHandle, useRef, useState } from "react";
import { Responsive, WidthProvider } from "react-grid-layout";
import { fetchDashboard, fetchTopics } from "../../../api/api";

import { CardTitle } from "../common";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";

const ResponsiveGridLayout = WidthProvider(Responsive);

const LoghookCard = React.forwardRef(
  ({ card, topic, messages, w, h, breakpoint }, ref) => {
    const cardRef = useRef(); // Reference to the original card
    const navigate = useNavigate();

    // Always call hooks unconditionally.
    const [rowHeight, setRowHeight] = useState(15);
    const [layouts, setLayouts] = useState(null);
    const [dashboardCards, setDashboardCards] = useState(null);
    const cols = { lg: 18, md: 12, sm: 8, xs: 5, xxs: 4 }; // Columns for each breakpoint

    // Use safeMessages from the passed messages prop.
    const safeMessages = messages && messages.length > 0 ? messages : [];
    // Compute token from safeMessages if available.
    const token =
      safeMessages.length > 0 && safeMessages[0].content
        ? safeMessages[0].content.split("/").pop()
        : null;

    // Define the action using the card prop.
    const action = () => {
      if (safeMessages.length > 0 && safeMessages[0].content) {
        navigate(`/${safeMessages[0].content.split("/").pop()}`);
      }
    };

    useImperativeHandle(ref, () => ({
      action,
    }));

    // Fetch dashboard cards and layouts using the token.
    useEffect(() => {
      if (token) {
        fetchDashboard(token).then(async (data) => {
          if (data.layouts.grid && data.layouts.grid[breakpoint]) {
            data.layouts.grid[breakpoint].forEach((layout) => {
              layout.static = true;
            });
          }
          setLayouts(data.layouts.grid);
          setDashboardCards(data.cards);
        });
      }
    }, [token, breakpoint]);

    useEffect(() => {
      const calculateRowHeight = () => {
        if (cardRef.current) {
          const cardWidth = cardRef.current.offsetWidth;
          const columnWidth = cardWidth / cols[breakpoint];
          setRowHeight(columnWidth * 0.95);
        }
      };

      calculateRowHeight(); // Initial calculation
      window.addEventListener("resize", calculateRowHeight);
      return () => {
        window.removeEventListener("resize", calculateRowHeight);
      };
    }, [breakpoint, cols]);

    // Instead of early-returning if token is missing, render a fallback UI.
    if (!token) {
      return <div>Loading...</div>;
    }

    if (!layouts || !dashboardCards || !breakpoint) {
      return <div>Loading...</div>;
    }

    return (
      <div className="flex flex-col justify-top text-center bg-gray-50 h-full">
        <CardTitle title={topic.name} />
        <div
          ref={cardRef}
          className="w-content h-content disabled cursor-pointer"
        >
          <ResponsiveGridLayout
            className="card-layout"
            layouts={{ breakpoint: layouts[breakpoint] }}
            breakpoints={{ breakpoint: 0 }}
            cols={{ breakpoint: cols[breakpoint] }}
            rowHeight={rowHeight}
            compactType={null}
            preventCollision={true}
            isResizable={false}
            margin={[1, 1]}
          >
            {dashboardCards
              .filter((item) => item.type === "display")
              .map((item) => (
                <div key={item.id} className="grid-item shadow-md">
                  <div className="content w-full h-full bg-indigo-400">
                    <div className="cursor-pointer w-full h-full">
                      <div className="bg-red-100" />
                    </div>
                  </div>
                </div>
              ))}
          </ResponsiveGridLayout>
        </div>
      </div>
    );
  }
);

LoghookCard.propTypes = {
  card: PropTypes.object.isRequired,
  topic: PropTypes.object.isRequired,
  messages: PropTypes.array.isRequired,
  w: PropTypes.number.isRequired,
  h: PropTypes.number.isRequired,
  breakpoint: PropTypes.string.isRequired,
};

export default LoghookCard;
